import React, { useEffect, useRef, useState } from 'react';
import { Modal, CurrencyInput, Button } from '../ui';
import { Input } from '../ui/Input';
import { useExpenseStore } from '../../stores/useExpenseStore';
import { useAppStore } from '../../stores/useAppStore';

const QuickAddExpense = ({ isOpen, onClose }) => {
  const { createExpense } = useExpenseStore();
  const { addNotification } = useAppStore();
  const amountRef = useRef(null);
  const descriptionRef = useRef(null);

  const [amount, setAmount] = useState(0);
  const [description, setDescription] = useState('');
  const [amountError, setAmountError] = useState('');
  const [descriptionError, setDescriptionError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset and focus when modal opens
  useEffect(() => {
    if (isOpen) {
      setAmount(0);
      setDescription('');
      setAmountError('');
      setDescriptionError('');
      setTimeout(() => amountRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const validate = () => {
    let valid = true;
    if (!amount || amount <= 0) {
      setAmountError('Amount must be greater than 0');
      valid = false;
    } else {
      setAmountError('');
    }
    if (!description.trim()) {
      setDescriptionError('Description is required');
      valid = false;
    } else {
      setDescriptionError('');
    }
    return valid;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    try {
      setIsSubmitting(true);
      await createExpense({ Amount: amount, Description: description.trim() });
      addNotification({ type: 'success', message: 'Expense added successfully' });
      onClose();
    } catch {
      addNotification({ type: 'error', message: 'Failed to add expense' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add Expense" size="md" showCloseButton={true}>
      <p className="text-xs text-gray-500 mb-4">Ctrl+E &middot; Press Escape to close</p>

      {/* Amount */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Amount <span className="text-red-500">*</span>
        </label>
        <CurrencyInput
          ref={amountRef}
          value={amount}
          onChange={setAmount}
          min={0}
          placeholder="0.00"
          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          onKeyDown={handleKeyDown}
        />
        {amountError && <p className="mt-1 text-sm text-red-600">{amountError}</p>}
      </div>

      {/* Description */}
      <div className="mb-4">
        <Input
          ref={descriptionRef}
          label="Description *"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="e.g., Tea, Lunch, Supplies..."
          error={descriptionError}
          onKeyDown={handleKeyDown}
        />
      </div>

      {/* Buttons */}
      <div className="flex justify-end gap-3">
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : 'Save  ↵'}
        </Button>
      </div>

      <p className="text-xs text-gray-400 text-center mt-3">
        Tab to move between fields &middot; Enter to save
      </p>
    </Modal>
  );
};

export default QuickAddExpense;
